import feedbackType from '@/helpers/feedbackType';
import contentCategory from '~/helpers/contentCategory';
import contentType from '@/helpers/contentType';
import { verifyContentOfEditorIsEmpty } from '~/helpers/stringHelper';
import validRetryHelper from '@/helpers/validRetry';
import { RESULT_TYPES } from '@/helpers/avaliaActivity.js';
import CategoryType from '@/helpers/contentCategory';
import { hasTime } from '@/helpers/date';

export default {
  methods: {
    getDeadlineDateAcademicMainOrTopicDeadline(topic) {
      const formatDate = this.$options.filters.formatDate;
      const formatDateWithoutHour = this.$options.filters.formatDateWithoutHour;

      const date = topic.isAcademicMainExpired ? topic.academicMainDeadlineDate : topic.deadlineAt;

      if (!date) {
        return '';
      }

      return hasTime(date) ? formatDate(date) : formatDateWithoutHour(date);
    },
    isAvaliaExternalActivity(topic) {
      return topic.categoryTypeId === contentCategory.AVALIA;
    },
    isTopicExpired(topic) {
      return !topic.isBeforeDeadline;
    },
    isTopicDeadlineExpired(topic) {
      return topic.topicTypeId === contentType.PAGE ? !!topic.expiredChildrenCount : topic.expired;
    },
    topicIsAvailableToAnswer(topic) {
      return topic.isBeforeDeadline && !topic.isFuture;
    },
    topicIsHidden(topic) {
      return !topic.isVisible;
    },
    mustBeShownFeedback(topic) {
      return topic.content.mustBeShownFeedback;
    },
    isAvaliaResultNone(topic) {
      return topic.content.activityResultType === RESULT_TYPES.NO_RESULT;
    },
    isAvaliaResultPartial(topic) {
      return topic.content.activityResultType === RESULT_TYPES.PARTIAL;
    },
    isAvaliaResultFull(topic) {
      return topic.content.activityResultType === RESULT_TYPES.FULL;
    },
    isAvaliaResultDate(topic) {
      return topic.content.activityResultType === RESULT_TYPES.DATE;
    },
    isAvaliaResultDateAndShouldShowResult(topic) {
      return this.isAvaliaResultDate(topic) && this.mustBeShownFeedback(topic);
    },
    shouldShowResultActivity(topic) {
      return (
        this.isAvaliaResultPartial(topic) ||
        this.isAvaliaResultFull(topic) ||
        this.isAvaliaResultDateAndShouldShowResult(topic)
      );
    },
    isCompleted(topic) {
      switch (topic.categoryTypeId) {
        case CategoryType.ORGANIZATION:
          return topic.viewedChildrenCount === topic.childrenCountWithProgressRecord;
        case CategoryType.AVALIA:
          if (topic.content.hasRetries) {
            return this.studentAnsweredAllAttempts(topic);
          }

          return topic.viewed;
        default:
          return topic.viewed;
      }
    },
    avaliaActivityAttemptsIsLinkEnabled(topic) {
      return this.shouldShowResultActivity(topic) && this.studentAnsweredAtLeastOneAttempt(topic);
    },
    isTopicExpiredAndNotMustBeShownFeedback(topic) {
      return this.isTopicExpired(topic) && !this.mustBeShownFeedback(topic);
    },
    isTopicExpiredAndMustBeShownFeedback(topic) {
      return this.isTopicExpired(topic) && this.mustBeShownFeedback(topic);
    },
    studentAnsweredAllAttempts(topic) {
      return topic.content.studentCurrentAttempt >= topic.content.numberRetries;
    },
    studentAnsweredAtLeastOneAttempt(topic) {
      return topic.content.studentCurrentAttempt > 0;
    },
    topicHasRetriesToAnswer(topic) {
      return this.topicHasRetries(topic) && !this.studentAnsweredAllAttempts(topic);
    },
    isTopicFinished(topic) {
      return (topic.categoryTypeId !== contentCategory.AVALIA && (topic.content?.hasCompletedAllAttempts || (this.isTopicExpired(topic) && topic.viewed))) || (topic.categoryTypeId === contentCategory.AVALIA && topic.viewed);
    },
    hasAttachment(topic) {
      return topic.content.hasFileUpload;
    },
    topicHasRetries(topic) {
      return topic.content.hasRetries;
    },
    validateMixinHasGrade(topic) {
      return topic.hasGrade && topic.grade;
    },
    getValidAttemptText(topic) {
      return {
        [validRetryHelper.HIGHEST] : this.$t('student.task.attemptsTable.validAttempt.biggestGrade'),
        [validRetryHelper.AVERAGE] : this.$t('student.task.attemptsTable.validAttempt.average'),
        [validRetryHelper.LAST]    : this.$t('student.task.attemptsTable.validAttempt.lastAttempt')
      }[topic.retryTypeId];
    },
    findCurrentAttempt(attemptsArray = null) {
      const attempts = attemptsArray || this.topic.content.attempts;

      if (attempts) {
        return attempts[attempts.length - 1];
      } else {
        return null;
      }
    },
    shouldShowFeedback(topic) {
      return (this.isTopicFinished(topic) || this.isTopicExpired(topic)) && topic.content.hasReleasedFeedback;
    },
    // NOTE: Métodos específicos para validações do quiz
    isOptionSelectedByStudent(questionId, optionId, attempt = null) {
      const currentAttempt = attempt || this.findCurrentAttempt(this.topic.content.attempts);

      if (currentAttempt && currentAttempt.answers && currentAttempt.answers.length > 0) {
        const isSelected = currentAttempt.answers.find((answer) => {
          return answer.questionId === questionId && answer.optionId === optionId;
        });

        return !!isSelected;
      }
    },
    hasFeedbackByOption(question) {
      return question.feedbackTypeId === feedbackType.BY_OPTION && question.options[0] && question.options[0].feedback && question.options[0].feedback.length > 0;
    },
    hasFeedbackByQuestion(question) {
      return question.feedbackTypeId === feedbackType.GENERAL && question.feedback && !verifyContentOfEditorIsEmpty(question.feedback);
    },
    isOptionCorrect(option) {
      return option.isCorrect;
    },
    findQuestionInCurrentAttemptAnswers(questionId, answersArray) {
      return answersArray.find((answer) => answer.questionId === questionId);
    },
    checkIfAllQuizQuestionsAreAnswered(attempt, quizQuestions) {
      return quizQuestions.every((question) => {
        const studentAttemptAnswer = this.findQuestionInCurrentAttemptAnswers(question.id, attempt.answers);

        return !!studentAttemptAnswer;
      });
    },
    returnNotAnsweredQuestions() {
      const currentAttempt = this.findCurrentAttempt(this.topic.content.attempts);
      const notAnsweredQuestions = [];

      if (currentAttempt && currentAttempt.answers) {
        if (currentAttempt.answers.length > 0) {
          this.topic.content.questions.forEach((question, questionIndex) => {
            const studentAnswer = this.findQuestionInCurrentAttemptAnswers(question.id, currentAttempt.answers);

            if (!studentAnswer) {
              notAnsweredQuestions.push(questionIndex + 1);
            }
          });
        } else {
          this.topic.content.questions.forEach((_, index) => {
            notAnsweredQuestions.push(index + 1);
          });
        }
      }

      return notAnsweredQuestions.join(', ');
    }
  }
};
